import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Popover, Button, Input, message } from "antd";
import type { TooltipPlacement } from "antd/es/tooltip";
import styles from "./index.module.less";

type FeedbackType = "like" | "unlike";
type ConfigType = "markdown" | "reference";

interface IFeedbackModalProps {
    /** 是否显示弹窗 */
    open: boolean;
    /** 反馈类型：like-点赞 unlike-点踩 */
    type: FeedbackType;
    /** 配置类型：markdown-普通回答 */
    configType?: ConfigType;
    /** 消息 ID  */
    messageId?: string;
    /** 弹窗配置（来自 feature.like/dislike 的 popupInfo.content） */
    popupConfig?: any;
    /** 关闭回调 */
    onClose: () => void;
    /** 提交回调 */
    onSubmit: (type: FeedbackType, selectedValues: string[], inputValue: string) => void;
    /** 触发元素 */
    children: React.ReactNode;
}

/**
 * 反馈弹窗组件
 * 用于点赞/点踩时选择原因
 * 弹窗出现在触发元素下方，无蒙层
 */
// 弹窗尺寸常量（与 CSS 保持一致）
const POPOVER_WIDTH = 700;
const POPOVER_HEIGHT_ESTIMATE = 300; // 预估高度

const FeedbackModal: React.FC<IFeedbackModalProps> = ({
    open = false,
    type,
    configType = "markdown",
    messageId,
    popupConfig,
    onClose,
    onSubmit,
    children,
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const [placement, setPlacement] = useState<TooltipPlacement>("bottomLeft");
    const [positionReady, setPositionReady] = useState(false);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState<string>("");
    const [submitting, setSubmitting] = useState(false);

    // 预先计算弹窗位置，避免渲染后再翻转导致的跳动
    useEffect(() => {
        if (!open) {
            setPositionReady(false);
            return;
        }

        const calculatePlacement = () => {
            if (!triggerRef.current) {
                setPositionReady(true);
                return;
            }

            const rect = triggerRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // 计算各方向的可用空间
            const spaceBottom = viewportHeight - rect.bottom;
            const spaceRight = viewportWidth - rect.left;

            // 决定垂直方向：优先底部，空间不足则顶部
            const vertical = spaceBottom >= POPOVER_HEIGHT_ESTIMATE ? "bottom" : "top";

            // 决定水平对齐：优先左对齐，空间不足则右对齐
            const horizontal = spaceRight >= POPOVER_WIDTH ? "Left" : "Right";

            setPlacement(`${vertical}${horizontal}` as TooltipPlacement);
            setPositionReady(true);
        };

        // 使用 requestAnimationFrame 确保在下一帧计算，获取准确的位置
        requestAnimationFrame(calculatePlacement);
    }, [open]);

    // 弹窗打开时重置选中状态
    useEffect(() => {
        if (!open) return;
        setSelectedValues([]);
        setInputValue("");
    }, [open]);

    // 切换选项选中状态
    const handleToggleOption = (value: string) => {
        setSelectedValues((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    };

    // 提交反馈
    const handleSubmit = useCallback(async () => {
        if (selectedValues.length === 0 && !inputValue.trim()) {
            message.warning("请选择原因或输入其他原因");
            return;
        }

        try {
            setSubmitting(true);
            onSubmit(type, selectedValues, inputValue);
            onClose();
        } catch (error) {
            console.error("提交反馈失败:", error);
            message.error("提交失败，请重试");
        } finally {
            setSubmitting(false);
        }
    }, [selectedValues, inputValue, onSubmit, type, onClose]);
    const title = popupConfig?.title || (type === "like" ? "请选择满意的原因" : "请选择不满意的原因");

    const optionItems = useMemo<string[]>(() => {
        const items: string[] = [];
        const contentArray = Array.isArray(popupConfig?.content) ? popupConfig.content : [];
        for (const group of contentArray) {
            if (group?.items) {
                for (const item of group.items) {
                    if (item?.value) items.push(item.value);
                }
            }
        }
        return items;
    }, [popupConfig]);

    const inputPlaceholder = popupConfig?.input?.placeholder || "请输入其他原因";

    const content = (
        <div className={styles.content}>
            {/* 标题区域 */}
            <div className={styles.header}>
                <div className={styles.headerTop}>
                    <h3 className={styles.title}>{title}</h3>
                    <img src="https://muzhi-public-pic.cdn.bcebos.com/100115336879" alt="" onClick={onClose} />
                </div>
                <p className={styles.subtitle}>这将有助于我们做的更好</p>
            </div>

            {/* 选项区域 */}
            {optionItems.length > 0 && (
                <div className={styles.optionsGrid}>
                    {optionItems.map((item) => (
                        <button
                            key={item}
                            className={`${styles.optionItem} ${selectedValues.includes(item) ? styles.selected : ""}`}
                            onClick={() => handleToggleOption(item)}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            )}

            {/* 输入框 */}
            <div className={styles.inputWrapper}>
                <Input
                    className={styles.feedbackInput}
                    placeholder={inputPlaceholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    maxLength={500}
                />
            </div>

            {/* 提交按钮 */}
            <div className={styles.footer}>
                <Button
                    type="primary"
                    className={styles.submitBtn}
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={selectedValues.length === 0 && !inputValue.trim()}
                >
                    {popupConfig?.submitBtn?.value || "提交"}
                </Button>
            </div>
        </div>
    );

    return (
        <div ref={triggerRef} style={{ display: "inline-block" }}>
            <Popover
                open={open && positionReady}
                onOpenChange={(visible) => !visible && onClose()}
                content={content}
                trigger={[]}
                placement={placement}
                classNames={{ root: styles.feedbackPopover }}
                arrow={false}
                destroyOnHidden
                autoAdjustOverflow={false}
            >
                {children}
            </Popover>
        </div>
    );
};

export default FeedbackModal;
